import { client } from '@repo/sdk-generated/client'
import type { QueryClient } from '@tanstack/react-query'
import { envVars } from './env-vars'

type GetCsrfToken = (queryClient: QueryClient) => string | null | undefined
type ShouldSkipUnauthorizedRecovery = (pathname: string) => boolean

interface BrowserSdkConfig {
	queryClient?: QueryClient
	onUnauthorized?: () => Promise<void> | void
	getCsrfToken?: GetCsrfToken
	shouldSkipUnauthorizedRecovery?: ShouldSkipUnauthorizedRecovery
	requestInterceptorId?: number
	responseInterceptorId?: number
}

interface BrowserSdkConfigOptions {
	queryClient: QueryClient
	onUnauthorized: () => Promise<void> | void
	/**
	 * App-provided reader for the CSRF token; the app knows which query key
	 * holds the session (client vs admin). Returns the token or nullish.
	 */
	getCsrfToken: GetCsrfToken
	/**
	 * App-provided predicate for paths whose 401s must not trigger session
	 * recovery (auth endpoints themselves).
	 */
	shouldSkipUnauthorizedRecovery: ShouldSkipUnauthorizedRecovery
}

const BROWSER_SDK_CONFIG_KEY = Symbol.for('sealedger.browser-sdk-config')

type ConfigurableBrowserSdkClient = typeof client & {
	[BROWSER_SDK_CONFIG_KEY]?: BrowserSdkConfig
}

export function configureBrowserSdk({
	queryClient,
	onUnauthorized,
	getCsrfToken,
	shouldSkipUnauthorizedRecovery
}: BrowserSdkConfigOptions) {
	if (typeof window === 'undefined') {
		return
	}

	const config = getBrowserSdkConfig()
	config.queryClient = queryClient
	config.onUnauthorized = onUnauthorized
	config.getCsrfToken = getCsrfToken
	config.shouldSkipUnauthorizedRecovery = shouldSkipUnauthorizedRecovery

	client.setConfig({
		baseUrl: resolveBrowserSdkBaseUrl(),
		headers: buildBrowserSdkBaseHeaders(),
		credentials: 'include'
	})

	if (
		config.requestInterceptorId === undefined ||
		!client.interceptors.request.exists(config.requestInterceptorId)
	) {
		config.requestInterceptorId =
			client.interceptors.request.use(addSessionToken)
	}

	if (
		config.responseInterceptorId === undefined ||
		!client.interceptors.response.exists(config.responseInterceptorId)
	) {
		config.responseInterceptorId =
			client.interceptors.response.use(handleUnauthorized)
	}
}

function getBrowserSdkConfig() {
	const sdkClient = client as ConfigurableBrowserSdkClient
	sdkClient[BROWSER_SDK_CONFIG_KEY] ??= {}
	return sdkClient[BROWSER_SDK_CONFIG_KEY]
}

function readBrowserSdkConfig() {
	return (client as ConfigurableBrowserSdkClient)[BROWSER_SDK_CONFIG_KEY]
}

function addSessionToken(request: Request) {
	const headers = new Headers(request.headers)
	const config = readBrowserSdkConfig()
	const queryClient = config?.queryClient
	const token = queryClient ? config?.getCsrfToken?.(queryClient) : undefined

	if (!token) {
		return request
	}

	headers.set('X-CSRF-Token', token)

	return new Request(request, { headers })
}

async function handleUnauthorized(response: Response) {
	if (response.status !== 401) {
		return response
	}

	if (isUnauthorizedRecoverySkipped(response.url)) {
		return response
	}

	const config = readBrowserSdkConfig()
	config?.queryClient?.clear()
	await config?.onUnauthorized?.()

	return response
}

function resolveBrowserSdkBaseUrl() {
	// Dev: route through the Vite dev-proxy `/api` namespace so backend calls
	// stay same-origin (localhost) and auth cookies work. The proxy strips
	// `/api` before forwarding, so the backend still sees the SDK's bare paths.
	if (import.meta.env.DEV) {
		return '/api'
	}

	return envVars.VITE_API_ENDPOINT
}

function buildBrowserSdkBaseHeaders(): Record<string, string> {
	if (!import.meta.env.DEV) {
		return {}
	}

	return {
		'ngrok-skip-browser-warning': 'true'
	}
}

function isUnauthorizedRecoverySkipped(url: string) {
	const pathname = new URL(url, window.location.origin).pathname
	return (
		readBrowserSdkConfig()?.shouldSkipUnauthorizedRecovery?.(pathname) ??
		false
	)
}
